// Карточка долга: история удержаний, график, состояние (тз, раздел 5.5).
//
// ## Почему история обязательна
//
// Долг — это обещание, растянутое на месяцы, и главный вопрос по нему не «сколько осталось», а
// «удерживали ли вообще». Пока истории не было, ответ собирался сверкой ведомостей за полгода;
// человеку при этом говорили «удерживаем», а он видел, что сумма не двигается.
//
// ## Панель, а не экран
//
// Долг открывают ИЗ реестра, чтобы вернуться к реестру: список остаётся на месте, отбор не
// теряется. Отдельный экран заставил бы возвращаться кнопкой назад и заново накладывать отбор.
//
// ## Почему списание появилось здесь 15.09.2026
//
// Панель была целиком на чтение, а списание — исключительное право руководства — жило только
// в карточке человека. Генеральный директор, глядя в реестр долгов, не мог сделать с долгом
// ничего: чтобы списать, он уходил в «Люди», искал ту же фамилию, открывал карточку, вкладку
// «Долги» и там находил ту же строку. Четыре нажатия, переход в чужой раздел и потерянный
// отбор реестра — ради действия над объектом, который уже открыт перед ним.
//
// Правило 3 говорит ровно об этом: действие живёт там, где объект. Теперь из реестра долг
// списывается двумя нажатиями — строка и «Списать».
//
// ⚠️ Второго окна подтверждения при этом НЕ заведено, и это принципиально: окно то же самое
// (`WriteOffDialog`), с тем же обязательным основанием и той же названной суммой. Два входа в
// необратимое денежное действие допустимы, два РАЗНЫХ окна — нет: они разошлись бы текстом,
// и человек читал бы про последствия списания по-разному в зависимости от того, откуда зашёл.

import { useMemo, useState } from 'react';

import { Link } from 'react-router';

import { RiCalendarScheduleLine, RiExternalLinkLine } from '@remixicon/react';

// Окно списания берётся из раздела «Люди» как есть. Импорт через границу модуля здесь
// осознанный: у продукта одно списание долга, и копия ради двух кнопок означала бы второй
// набор слов про невозвратные потери.
import { WriteOffDialog } from '@/modules/people/card/WriteOffDialog';
import type { DebtOut } from '@/shared/api/endpoints/losses';
import type { СостояниеГрафика } from '@/shared/debts/ПоляГрафика';
import { ПоляГрафика, ПУСТОЙ_ГРАФИК, разобратьГрафик } from '@/shared/debts/ПоляГрафика';
import { useDebtScheduleTypes } from '@/shared/dictionaries/debt-schedule-types';
import { useDebtTypes } from '@/shared/dictionaries/debt-types';
import { useAuthStore } from '@/shared/auth/store';
import { formatKopecks, formatRubles } from '@/shared/money/format';
import { can } from '@/shared/rbac/permissions';
import { countLabel } from '@/shared/text/plural';
import { formatDateOnly } from '@/shared/time/format';
import * as Badge from '@/shared/ui/badge';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import { FieldList, FieldRow } from '@/shared/ui/field-row';
import { TextField } from '@/shared/ui/text-field';
import { EmptyState, ScreenState, Skeleton } from '@/shared/ui/screen-state';

import { useDebtsRegistry, useRepayments, useSetDebtSchedule } from '../../queries';

/** Списание — исключительное право руководства и администратора (`roles.yaml`, exclusive). */
const WRITE_OFF_PERMISSION = 'core.debt.write_off';

/** График меняет тот же, кто заводит долг: это условия, а не признание потери. */
const EDIT_PERMISSION = 'core.debt.edit';

export function DebtPanel({ id }: { id: string }) {
  // Готовое поле хранилища, а не `state.user?.permissions ?? []`: новый пустой массив на
  // каждый вызов селектора зацикливает отрисовку на zustand 5 (пояснение — в MoneySection).
  const права = useAuthStore((state) => state.permissions);
  // Долг берём из ТОГО ЖЕ реестра, что и экран: второго запроса за одной строкой в контракте
  // нет, и заводить его нельзя — это был бы второй источник одной суммы.
  const реестр = useDebtsRegistry();
  const удержания = useRepayments(id);
  const [правимГрафик, setПравимГрафик] = useState(false);
  const долг = useMemo<DebtOut | undefined>(
    () => реестр.data?.items.find((строка) => строка.id === id),
    [реестр.data, id],
  );

  if (реестр.isPending) {
    return <Skeleton className='m-4 h-40' />;
  }

  if (долг === undefined) {
    return (
      <div className='p-4'>
        <EmptyState
          title='Долг не найден'
          description='Он мог быть погашен и уйти из отбора «только непогашенные», либо его списали. Снимите отбор в реестре и откройте заново.'
        />
      </div>
    );
  }

  const удержано = долг.initial_amount - долг.outstanding;

  return (
    <div className='flex flex-col gap-4 p-4'>
      <div className='flex items-start justify-between gap-3'>
        <div className='flex min-w-0 flex-col gap-0.5'>
          <span className='truncate text-label-sm text-text-strong-950'>{долг.employee_name}</span>
          <span className='text-paragraph-xs text-text-sub-600'>{долг.debt_type_title}</span>
        </div>
        <Badge.Root variant='lighter' color={долг.is_blocking ? 'orange' : 'gray'} size='medium'>
          {долг.status_title}
        </Badge.Root>
      </div>

      {/* Действия над открытым долгом. Стоят под шапкой панели, а не внизу за историей
          удержаний: до конца истории надо доскроллить, а решение принимают, глядя на остаток.

          Первичной кнопки здесь НЕТ ни одной, и это не упущение. Списание идёт приглушённым
          видом (его несёт само окно, `WriteOffDialog`): признание потери денег не должно
          звать к себе с экрана — на него приходят, уже решив. Ссылка в карточку выглядит
          обычной обводочной кнопкой, как «Карточка объекта» в разделе «Объекты»: переход
          между карточками по всему продукту выглядит одинаково.

          Подписана она тем, КУДА ведёт, а не словом «Открыть»: из реестра долгов в карточку
          идут за остальными долгами того же человека, поэтому и вкладка задана явно. */}
      <div className='flex flex-wrap items-center gap-2'>
        {/* Списываются только действующие долги с остатком. У погашенного и уже списанного
            кнопки нет вовсе: списывать там нечего, и объяснять нечего — состояние написано
            меткой в шапке. Условие то же, что в карточке человека; разъехавшись, они дали бы
            кнопку в одном месте и её отсутствие в другом у одного и того же долга. */}
        {долг.status === 'active' && долг.outstanding > 0 && (
          <WriteOffDialog
            долг={долг}
            employeeId={долг.employee_id}
            можно={can(права, WRITE_OFF_PERMISSION)}
          />
        )}
        {/* График правится там, где на него смотрят. До 15.09.2026 задать его было нечем
            вовсе: поле в карточке только показывало значение, а способов в системе было три
            из пяти обещанных. Кнопка стоит рядом со списанием и такая же приглушённая:
            изменение условий это не первичное действие карточки. */}
        {долг.status === 'active' && (
          <Button.Root
            variant='neutral'
            mode='stroke'
            size='xsmall'
            onClick={() => setПравимГрафик((было) => !было)}
          >
            <Button.Icon as={RiCalendarScheduleLine} />
            {правимГрафик ? 'Не менять график' : 'Изменить график'}
          </Button.Root>
        )}
        <Button.Root variant='neutral' mode='stroke' size='xsmall' asChild>
          {/* Адрес карточки собирается здесь строкой, а не через `peopleHref`: тащить ради
              одной ссылки разбор представлений чужого раздела дороже, чем сама ссылка.
              Вкладка задана явно — из реестра долгов в карточку идут за ОСТАЛЬНЫМИ долгами
              того же человека, а не за общими сведениями о нём. */}
          <Link to={`/people/employees/r/${долг.employee_id}?tab=debts`}>
            <Button.Icon as={RiExternalLinkLine} />
            Карточка человека
          </Link>
        </Button.Root>
      </div>

      <FieldList>
        <FieldRow label='Остаток' value={`${formatRubles(долг.outstanding)}`} />
        <FieldRow
          label='Удержано'
          value={`${formatRubles(удержано)} из ${formatRubles(долг.initial_amount)}`}
        />
        {/* Фраза приходит с сервера целиком, и это не лень экрана.

            Значение графика лежит в одном поле, а единица у него меняется вместе со способом:
            копейки, месяцы или проценты. Пока фразу собирал экран, он печатал «{значение} за
            расчёт» для любого способа — то есть у долга с погашением за полгода написал бы
            «6 ₽ за расчёт».

            Вторая половина вранья была в пустом значении: экран обещал «удержится целиком в
            ближайшем расчёте», а каскад в этом случае не удерживает НИЧЕГО и выносит долг в
            панель проверки. Человек читал успокоительную фразу и уходил, а долг висел. */}
        <FieldRow label='Как гасим' value={долг.schedule_note} />
        <FieldRow label='Заведён' value={formatDateOnly(долг.created_at)} />
        <FieldRow label='Основание' value={долг.basis ?? null} missing='основание не записано' />
        {долг.description ? <FieldRow label='Описание' value={долг.description} /> : null}
      </FieldList>

      {правимГрафик && (
        <ФормаГрафика
          долг={долг}
          можно={can(права, EDIT_PERMISSION)}
          onГотово={() => setПравимГрафик(false)}
        />
      )}

      <div className='flex flex-col gap-2'>
        <span className='text-label-sm text-text-strong-950'>История удержаний</span>
        <ScreenState
          data={удержания.data}
          error={удержания.error}
          subject='историю удержаний по долгу'
          isEmpty={(данные) => данные.items.length === 0}
          onRetry={() => void удержания.refetch()}
          loading={<Skeleton className='h-16 w-full' />}
          empty={
            <span className='text-paragraph-sm text-text-sub-600'>
              По этому долгу пока ничего не удерживали. Удержание появится здесь, когда пройдёт
              ближайший расчёт: график применяется при расчёте ведомости, а не в день заведения.
            </span>
          }
        >
          {(данные) => (
            <>
              <span className='text-paragraph-xs text-text-sub-600'>
                {countLabel(данные.items.length, ['удержание', 'удержания', 'удержаний'])} на{' '}
                {formatRubles(данные.items.reduce((сумма, строка) => сумма + строка.amount, 0))}
              </span>
              <ul className='flex flex-col divide-y divide-stroke-soft-200 rounded-xl border border-stroke-soft-200'>
                {данные.items.map((строка) => (
                  <li key={строка.id} className='flex items-center justify-between gap-3 px-3 py-2'>
                    <div className='flex min-w-0 flex-col'>
                      <span className='text-paragraph-sm text-text-strong-950'>
                        {formatDateOnly(строка.created_at)}
                      </span>
                      <span className='text-paragraph-xs text-text-sub-600'>
                        {/* Удержание из ведомости и удержание вне её — разные факты: у первого
                            есть расчёт, за которым можно пойти, у второго только основание. */}
                        {строка.payroll_employee_id ? 'из расчёта по ведомости' : 'вне ведомости'}
                      </span>
                    </div>
                    <span className='shrink-0 tabular-nums text-label-sm text-text-strong-950'>
                      {formatKopecks(строка.amount)}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </ScreenState>
      </div>
    </div>
  );
}

/**
 * Смена графика погашения у заведённого долга (тз, раздел 5.5).
 *
 * ## Почему прямо в панели, а не окном
 *
 * Окно поверх панели — это второй слой над вторым слоем, и закрывается он обратно в панель,
 * из-под которой человека уже увели. У необратимых денежных действий окно оправдано: оно
 * заставляет остановиться (так сделано списание). Здесь останавливать не надо — сумма долга
 * не меняется ни на рубль, меняется только то, как её забирают, и ошибка правится следующей
 * правкой.
 *
 * ## Почему основание обязательно
 *
 * График решает, сколько человек получит на руки в ближайшие месяцы. Через месяц на разборе
 * спрашивают не «какой график», а «почему поменяли»: ответ на первое видно и так, ответа на
 * второе без этого поля нет нигде.
 */
function ФормаГрафика({
  долг,
  можно,
  onГотово,
}: {
  долг: DebtOut;
  можно: boolean;
  onГотово: () => void;
}) {
  const способы = useDebtScheduleTypes();
  const типы = useDebtTypes();
  const сменить = useSetDebtSchedule(долг.employee_id);

  // Прежний график подставлен в поля: график чаще правят, чем задают с нуля, и заставлять
  // человека вспоминать, что стояло, значит получить «примерно то же самое, но на рубль
  // меньше». Значение показывается в тех же единицах, в каких его вводят: рубли для суммы,
  // голое число для месяцев и процентов.
  const [график, setГрафик] = useState<СостояниеГрафика>(() => ({
    тип: долг.schedule_type || ПУСТОЙ_ГРАФИК.тип,
    значение:
      долг.schedule_value === null || долг.schedule_value === undefined
        ? ''
        : долг.schedule_type === 'per_payroll'
          ? // Через общий формат, а не делением на сто: своё деление уже стоило дефекта в
            // передаче подотчёта — сумма показывалась в чужом виде и на копейки мимо. Пара
            // `formatKopecks` и `parseRublesToKopecks` замкнута: что показали, то и разберём.
            formatKopecks(долг.schedule_value)
          : String(долг.schedule_value),
  }));
  const [основание, setОснование] = useState('');
  const [ошибки, setОшибки] = useState<{ тип?: string; значение?: string; основание?: string }>({});

  const умолчание = (типы.data?.items ?? []).find((строка) => строка.code === долг.debt_type)
    ?.default_schedule_value;

  if (!можно) {
    // Не серая кнопка и не спрятанная форма: и то и другое читается как поломка. Человек
    // видит, чего не хватает и к кому идти (`ПРАВИЛА.md`, правило 10).
    return (
      <div className='rounded-lg bg-bg-weak-50 p-3 text-paragraph-sm text-text-sub-600'>
        График погашения меняет финотдел. Своего доступа к этому у вас нет: попросите их или
        администратора.
      </div>
    );
  }

  return (
    <form
      className='flex flex-col gap-3 rounded-xl border border-stroke-soft-200 p-3'
      noValidate
      onSubmit={(событие) => {
        событие.preventDefault();
        const разбор = разобратьГрафик(график, способы.data?.items ?? []);
        const next: { тип?: string; значение?: string; основание?: string } = {};
        if (!разбор.ok) {
          next[разбор.поле] = разбор.ошибка;
        }
        if (основание.trim().length < 3) {
          next.основание =
            'Основание обязательно: через месяц по нему объясняют, почему удерживают именно столько.';
        }
        if (Object.keys(next).length > 0 || !разбор.ok) {
          setОшибки(next);
          return;
        }
        setОшибки({});
        сменить.mutate(
          {
            debtId: долг.id,
            body: {
              schedule_type: разбор.schedule_type,
              schedule_value: разбор.schedule_value,
              basis: основание.trim(),
            },
          },
          { onSuccess: onГотово },
        );
      }}
    >
      <ПоляГрафика
        id='debt-panel'
        состояние={график}
        onChange={setГрафик}
        способы={способы.data}
        загружаются={способы.isPending}
        ошибкаСправочника={способы.error}
        умолчаниеКопеек={умолчание}
        ошибки={ошибки}
      />
      <TextField
        id='debt-panel-schedule-basis'
        label='Почему меняем'
        required
        value={основание}
        onChange={setОснование}
        placeholder='договорились гасить за полгода, заявление от 12.09'
        hint='Останется в журнале вместе с прежним и новым условием.'
        error={ошибки.основание}
      />

      {сменить.isError && <ErrorAlert error={сменить.error as never} />}

      <div className='flex items-center gap-2'>
        <Button.Root type='submit' variant='primary' mode='filled' size='xsmall'>
          {сменить.isPending ? 'Сохраняем…' : 'Сохранить график'}
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='xsmall' onClick={onГотово}>
          Отмена
        </Button.Root>
      </div>
    </form>
  );
}
